import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  explanationBullets,
  headline,
} from "@aucash/rules/default-super-insurance";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Checkbox, Field, OptionList, Stepper } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space } from "../../src/theme";

// default-super-insurance — the "default insurance you may be silently paying
// for inside super" AWARENESS checker, surfaced stateless-v1 (PLAN arch
// decision #1). One screen; the result renders INLINE (no save-by-id, no auth
// gate, no persistence). Each run is a stateless
// `calc.defaultSuperInsurance.compute` query — coarse, NON-identifying inputs
// (age / balance band / inactivity / account count) -> rule-explanation output.
//
// REGULATORY POSTURE (binding): default cover inside super is a Corporations
// Act s764A financial product with no carve-out. So this surface is INFORMATION
// only — it explains WHICH default-cover rules apply to an account like the one
// described. It emits NO dollar/premium/coverage number, NO fund/super-product
// identifier, and NEVER a keep/cancel/buy/switch steer. All wording comes
// verbatim from the rules layer's copy helpers (so it can't drift from the API/
// SEO surfaces and so the rules-layer guards apply), and the next steps point
// only to neutral government/PDS actions (check your statement / myGov / your
// fund's PDS) — never a product handoff.

const ASSESSMENT_YEAR = "2025-26";

// Full disclaimer rendered above the first result statement (validation §3.2
// placement rule). Kept here (the rules copy.ts keeps DISCLAIMER internal) so it
// can sit at the top of the result block.
const FULL_RESULT_DISCLAIMER =
  "Educational tool. Not financial advice. This tool explains how the default-cover rules under Putting Members' Interests First and Protecting Your Super apply to an account like the one you describe. It does not tell you what your own account holds, and it is not a recommendation to keep, cancel, switch or buy any insurance. Insurance inside super is a financial product — to decide anything about your own cover, check your fund's statement or product disclosure statement, or speak to a licensed adviser.";

type BalanceBand = "under6k" | "sixToFiftyK" | "fiftyKPlus" | "unknown";

const BALANCE_OPTIONS: ReadonlyArray<{
  value: BalanceBand;
  label: string;
  sub: string;
}> = [
  {
    value: "under6k",
    label: "Under A$6,000",
    sub: "Below the Putting Members' Interests First threshold.",
  },
  {
    value: "sixToFiftyK",
    label: "A$6,000 – under A$50,000",
    sub: "At or above the A$6,000 threshold.",
  },
  {
    value: "fiftyKPlus",
    label: "A$50,000 and over",
    sub: "At or above the A$6,000 threshold.",
  },
  {
    value: "unknown",
    label: "I'm not sure",
    sub: "We'll explain the rule without assuming where you sit.",
  },
];

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.defaultSuperInsurance.compute.query>
>;

export default function DefaultSuperInsuranceIndex(): JSX.Element {
  const router = useRouter();

  const [age, setAge] = useState(35);
  const [balanceBand, setBalanceBand] = useState<BalanceBand | null>(null);
  const [knowsInactivity, setKnowsInactivity] = useState(false);
  const [inactiveMonths, setInactiveMonths] = useState(0);
  const [activeAccountCount, setActiveAccountCount] = useState(1);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (balanceBand === null)
      next.balanceBand = "Pick the band your account balance sits in.";
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0 || balanceBand === null || submitting)
      return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.defaultSuperInsurance.compute
      .query({
        input: {
          age,
          balanceBand,
          inactiveMonths: knowsInactivity ? inactiveMonths : null,
          activeAccountCount,
          assessmentYear: ASSESSMENT_YEAR,
        },
      })
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the check.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Default super insurance" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>FY26 · insurance inside super</Eyebrow>
            <Text style={styles.title}>
              Default insurance you may be paying for inside super
            </Text>
            <Text style={styles.subtitle}>
              Super funds add life, TPD and income-protection cover to many
              accounts by default — and the premiums come out of your balance.
              Answer a few non-identifying questions to see which default-cover
              rules apply to an account like yours. This explains the rules; it
              never tells you to keep or cancel any cover.
            </Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field
            label="Your age"
            help="Default (opt-out) cover is only provided from age 25 under the rules."
          >
            <Stepper
              value={age}
              onChange={setAge}
              min={15}
              max={75}
              suffix="years"
              testID="input-age"
              accessibilityLabel="Your age"
            />
          </Field>

          <Field
            label="Roughly what is this account's balance?"
            help="We never ask for an exact balance or a fund name — only the band."
            error={errors.balanceBand}
          >
            <OptionList<BalanceBand>
              options={BALANCE_OPTIONS}
              value={balanceBand}
              onChange={(v) => {
                setBalanceBand(v);
                setResult(null);
              }}
              testIDPrefix="balance"
            />
          </Field>

          <Field label="Do you know how long since this account last received a contribution or rollover?">
            <Checkbox
              checked={knowsInactivity}
              onChange={setKnowsInactivity}
              label="Yes — I can estimate the months of inactivity."
            />
          </Field>

          {knowsInactivity ? (
            <Field
              label="Months since the last contribution or rollover"
              help="Funds switch insurance off after 16 months of inactivity unless you elect to keep it."
            >
              <Stepper
                value={inactiveMonths}
                onChange={setInactiveMonths}
                min={0}
                max={60}
                suffix="months"
                testID="input-inactive-months"
                accessibilityLabel="Months of inactivity"
              />
            </Field>
          ) : null}

          <Field
            label="How many active super accounts do you hold?"
            help="Holding more than one can mean each account charges its own premiums."
          >
            <Stepper
              value={activeAccountCount}
              onChange={setActiveAccountCount}
              min={1}
              max={10}
              suffix="accounts"
              testID="input-account-count"
              accessibilityLabel="Number of active super accounts"
            />
          </Field>

          <Button
            label={submitting ? "Checking…" : "See which rules apply →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="default-super-insurance-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer sits at the TOP, before any result statement (validation
 * §3.2 placement rule). All copy comes from the rules-layer helpers, so it can
 * never drift into advice and the rules-layer guards apply to every string. */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  const bullets = explanationBullets(
    result.output,
    result.fy,
    result.sourcesVerifiedAt,
  );

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any result statement. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{FULL_RESULT_DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>How the rules apply · FY26</Eyebrow>
        <Chip label="Information" tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{headline(result.output)}</Text>
      </Card>

      <Section title="What this means">
        {bullets.map((b, i) => (
          <Text key={i} style={styles.bodyText}>
            {b}
          </Text>
        ))}
      </Section>

      <Section title="Where to check your own position">
        <Text style={styles.bodyText}>
          This tool explains the rules — not what your own account holds. You can
          see your super accounts, balances and whether insurance is attached
          through myGov (linked to the ATO), or on each fund's statement and
          product disclosure statement.
        </Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · ruleset{" "}
          {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: -0.5,
    color: colors.ink,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
